import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  signal,
  ViewChild,
  computed,
  effect,
} from '@angular/core';
import {
  HsButtonModule,
  HsFilterOptionModel,
  HsFormFieldModule,
  HsInputModule,
  HsSearchBarModule,
} from '@hsetu/ngui-components';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { ArticleItem } from '@modules/shopping/models/article-item.model';
import { HsArticleCatalogComponent, HsArticleData } from '@hsetu/ngui-views';
import { KfmArticleCatalogDatasource } from '@modules/shopping/models/article-catalog-datasource.model';
import { ItemStockService } from '@modules/shopping/services/item-stock.service';
import { ItemStockItem } from '@modules/shopping/models/item-stock-item.model';
import { GuidGeneratorService } from '@shared/tools/guid-generator.service';
import {
  CANCEL_LABEL,
  OK_LABEL,
} from '@modules/shopping/i18n/label-text-constants';
import { KfmArticleDialogComponent } from '@modules/shopping/features/article-dialog/kfm-article-dialog.component';
import { KfmYesNoDialogComponent } from '@shared/ui/components/features/dialogs/yes-no-dialog/kfm-yes-no-dialog.component';
import { KaufmannSettingsService } from '@modules/settings/services/kfm-settings.service';

@Component({
  selector: 'kfm-article-catalog-dialog',
  standalone: true,
  imports: [
    CommonModule,
    HsSearchBarModule,
    HsButtonModule,
    HsInputModule,
    HsFormFieldModule,
    HsArticleCatalogComponent,
  ],
  templateUrl: './kfm-article-catalog-dialog.component.html',
  host: { class: 'kfm-article-catalog-dialog layer-two' },
})
export class KfmArticleCatalogDialogComponent {
  private readonly _itemStockService = inject(ItemStockService);
  private readonly _guidGenerator = inject(GuidGeneratorService);
  private readonly _dialog = inject(Dialog);
  private readonly _settings = inject(KaufmannSettingsService);

  private readonly _allLabelText = 'Alle';

  private _articleCatalogComponent: HsArticleCatalogComponent;
  private _hasSelectedArticles = signal(false);

  @ViewChild(HsArticleCatalogComponent)
  protected set articleCatalog(component: HsArticleCatalogComponent) {
    if (component) {
      this._articleCatalogComponent = component;
    }
  }

  protected readonly okLabel = OK_LABEL;
  protected readonly cancelLabel = CANCEL_LABEL;

  protected readonly articleCatalogDatasource =
    new KfmArticleCatalogDatasource();

  protected readonly dialogRef = inject<DialogRef<ArticleItem[]>>(
    DialogRef<ArticleItem[]>,
  );

  protected filterOptions = signal<HsFilterOptionModel[]>([]);

  protected canSave = computed(() => {
    // React to the signal change
    this._hasSelectedArticles();
    
    if (!this._articleCatalogComponent) {
      return false;
    }
    const items = this._articleCatalogComponent.getSelectedArticles();
    return items && items.length > 0;
  });

  constructor() {
    this._itemStockService.getManufacturers().then((manufacturers) => {
      const options: HsFilterOptionModel[] = [
        {
          id: this._allLabelText,
          label: this._allLabelText,
          selected: true,
        },
        ...manufacturers.map((manufacturer) => ({
          id: manufacturer,
          label: manufacturer,
          selected: false,
        })),
      ];

      this.filterOptions.set(options);
    });
  }

  protected onArticleInputChanged(): void {
    // Trigger the computed signal to re-evaluate
    const hasItems =
      this._articleCatalogComponent &&
      this._articleCatalogComponent.getSelectedArticles().length > 0;
    this._hasSelectedArticles.set(hasItems);
  }

  protected async onSaveClicked(): Promise<void> {
    if (!this._articleCatalogComponent) {
      this.dialogRef.close([]);
      return;
    }

    const selectedItems = this._articleCatalogComponent.getSelectedArticles();

    if (!selectedItems || selectedItems.length === 0) {
      this.dialogRef.close([]);
      return;
    }

    const items = await this._itemStockService.getByIds(
      selectedItems.map((item) => item.id),
    );

    const mappedItems: ArticleItem[] = items.map((i) => {
      const selectedItem = selectedItems.find((item) => item.id === i.uId);
      return {
        uId: this._guidGenerator.emptyUUID,
        tenantUId: this._guidGenerator.emptyUUID,
        name: '',
        number: '',
        userUId: this._guidGenerator.emptyUUID,
        concurrencyIdentificator: this._guidGenerator.emptyUUID,
        syncType: 0,
        concurrencyStamp: new Date().toISOString(),
        stampCreate: new Date().toISOString(),
        infoDesc: i.infoDesc,
        amount: selectedItem?.quantity || 1,
        unit: i.unit,
        articleNumber: i.articleNumber,
        manufacturerLabel: i.manufacturerLabel,
        taskId: undefined,
        priceTotal: {
          netTotal: i.priceTotal?.netTotal ?? 0,
          vat: i.priceTotal?.vat,
          vatTotal: i.priceTotal?.vatTotal,
          grossTotal: i.priceTotal?.grossTotal,
        },
      };
    });

    this.dialogRef.close(mappedItems);
  }

  protected async onEditItemClicked(item: HsArticleData): Promise<void> {
    // get the original item from item stock to get all details
    const itemStockItems = await this._itemStockService.getByIds([item.id]);
    const itemStockItem = itemStockItems[0];

    const dialogRef = this._dialog.open<ArticleItem>(
      KfmArticleDialogComponent,
      {
        width: '600px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        data: {
          item: itemStockItem,
          showAmount: true,
          showPrice: this._settings.showPrice,
        },
      },
    );

    dialogRef.closed.subscribe(async (result) => {
      if (result) {
        const updatedItem = await this._itemStockService.createOrUpdate(result);

        if (updatedItem) {
          await this.articleCatalogDatasource.dataSource.load();
        }
      }
    });
  }

  protected async onDeleteItemClicked(item: HsArticleData): Promise<void> {
    const dialogRef = this._dialog.open<boolean>(KfmYesNoDialogComponent, {
      width: '600px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      data: {
        title: 'Artikel löschen',
        text: 'Wollen sie den Artikel löschen?',
      },
    });

    dialogRef.closed.subscribe(async (result) => {
      if (result) {
        const ok = await this._itemStockService.delete(item.id);
        if (ok) {
          await this.articleCatalogDatasource.dataSource.load();
        }
      }
    });
  }
}
